import { useEffect, useId, useRef, useState, type CSSProperties } from 'react'
import CardLink from '../../components/CardLink'
import ModelIcon from '../../components/ModelIcon'
import { detectBrand } from '../../data/brands'
import BlockIcon from './BlockIcon'
import { cardAutoplayAllowed, useCardVideoAutoplay } from '../../utils/cardVideoAutoplay'
import { targetIsLive, type PublicTarget } from '../../utils/linkTarget'
import {
	showcaseColumns,
	mediaTextClasses,
	showcaseHover,
	showcaseTextStyle,
	showcaseImage,
	showcaseSlots,
	type ShowcaseCardItem,
	type ShowcaseConfig,
	type ShowcaseItem,
	type ShowcaseLead,
	type ShowcaseMedia,
	type ShowcasePresetItem,
} from './homeBlocks'

interface Props {
	blockId: string
	config: ShowcaseConfig
}

const GOAL = 'home_card_click'

/** Фон карточки: картинка (превью или оригинал — по размеру плитки) и/или ролик. Ролик играет только
 *  лёгким соседом `_loop` и только через общий менеджер автоплея (лимит одновременно играющих,
 *  экономия трафика, prefers-reduced-motion) — иначе остаётся постер. */
function CardMedia({ media, large }: { media?: ShowcaseMedia; large: boolean }) {
	const videoRef = useRef<HTMLVideoElement | null>(null)
	const loop = media?.video_loop_url || null
	// Тот же договор, что у плиток лент (TrendFeedSection.FeedVideo): играем только лёгкого соседа и
	// только когда автоплей уместен; src живёт вместе с игрой — без него браузер не тянет ни байта.
	const [managed] = useState(() => Boolean(loop) && cardAutoplayAllowed())
	const { active } = useCardVideoAutoplay(videoRef, managed)
	useEffect(() => {
		if (active) void videoRef.current?.play().catch(() => {})
	}, [active])
	if (!media) return null
	const image = showcaseImage(media, large)
	const poster = image?.src || media.video_preview_url || undefined
	return (
		<span className="hs-card__media" aria-hidden="true">
			{image && (
				<img
					src={image.src}
					alt=""
					loading="lazy"
					decoding="async"
					width={media.w}
					height={media.h}
					onError={(e) => {
						const img = e.currentTarget
						if (image.fallback && !img.dataset.fbk) {
							img.dataset.fbk = '1'
							img.src = image.fallback
						}
					}}
				/>
			)}
			{media.video_url && (managed || !image) && (
				<video
					ref={videoRef}
					// src снимается, когда менеджер забрал слот: так возвращается постер, а не замерший кадр.
					src={managed && active && loop ? loop : undefined}
					poster={poster}
					muted
					loop
					playsInline
					preload="none"
					data-autoplay={managed ? '1' : undefined}
					onError={(e) => { if (e.currentTarget.getAttribute('src')) e.currentTarget.style.display = 'none' }}
				/>
			)}
			<span className="hs-card__scrim" />
		</span>
	)
}

/** Размер плитки в сетке — классами, а не inline-стилем: на узком экране колонок меньше, и правило
 *  адаптива должно иметь возможность сузить плитку (inline `span 2` в сетке из одной колонки рвёт вёрстку).
 *  Ряд сетки — половина высоты карточки, поэтому обычная плитка занимает два шага, высокая — четыре,
 *  трёхрядная — шесть. «Узкая» — полплитки, стоит в своей ячейке `.hs-slot` (см. showcaseSlots и _home-showcase.scss). */
const spanClass = (item: { cols: number; rows: number; short?: boolean }): string =>
	`${item.cols === 2 ? ' is-c2' : ''}${item.short ? ' is-short' : item.rows === 3 ? ' is-r3' : item.rows === 2 ? ' is-r2' : ''}`

/** Логотип бренда модели перед названием строки: у «Veo 3.1» — значок Veo, у «Suno V5» — Suno.
 *  Бренд определяется тем же правилом, что и везде на сайте (data/brands.ts), поэтому новая модель
 *  получает значок сама. Строка ведёт не на модель или бренд неизвестен — строка просто без значка.
 *  Рисует общий ModelIcon: он же знает, какой одноцветный логотип инвертировать на тёмной теме. */
function LineIcon({ target }: { target?: PublicTarget }) {
	const ref = target?.kind === 'model' ? target.ref ?? '' : ''
	const at = ref.indexOf(':')
	const brand = at > 0 ? detectBrand(ref.slice(0, at), ref.slice(at + 1)) : null
	if (!brand) return null
	return <ModelIcon icon={brand.icon} name="" size={16} className="hs-line__icon" />
}

function StaffTag({ target }: { target?: PublicTarget }) {
	if (!target) return null
	if (target.state) {
		return <span className="hs-tag hs-tag--warn">{target.state === 'hidden' ? 'закрыто клиентам' : 'цель недоступна'}</span>
	}
	if (target.staff_only) return <span className="hs-tag">видите только вы</span>
	return null
}

function Card({ item, blockId, textStyle }: { item: ShowcaseCardItem; blockId: string; textStyle?: string }) {
	const hasMedia = !!(item.media?.image_url || item.media?.video_url)
	const large = !item.short && (item.cols === 2 || item.rows >= 2 || item.style === 'spotlight')
	const lines = item.lines ?? []
	// Текст на кадре — только у плитки с кадром: у плитки на тинте и без фона текст и так на своём цвете.
	const tm = hasMedia ? mediaTextClasses(showcaseTextStyle(item.text_style, textStyle), item.media?.tone) : ''
	const cls = `hs-card hs-card--${item.style}${spanClass(item)}${hasMedia ? ' has-media' : ''}${item.tint && !hasMedia ? ' has-tint' : ''}${tm}`
	const style = (item.tint && !hasMedia ? { '--hs-tint': item.tint } : undefined) as CSSProperties | undefined
	const goal = { name: GOAL, params: { block: blockId, card: item.id, title: item.title } }

	const body = (
		<>
			<CardMedia media={item.media} large={large} />
			{/* Текст плитки — название и описание — одной группой: в «Плашке» это и есть плашка, в остальных
			    режимах обёртка `display: contents` и на раскладку не влияет. */}
			<span className="hs-card__copy">
				{/* Иконка — часть строки названия, а не отдельный ярус: так плитка читается одним блоком. */}
				<span className="hs-card__head">
					<BlockIcon icon={item.icon} className="hs-card__icon" />
					<span className="hs-card__title">{item.title}</span>
					{item.badge && <span className="hs-card__badge">{item.badge}</span>}
					<StaffTag target={item.target} />
				</span>
				{/* Обёртка описания: в обычных режимах это `display: contents` и на раскладку не влияет,
				    а в режиме «описание» — она и раскрывает текст на наведении. */}
				{item.text && (
					<span className="hs-card__reveal">
						<span className="hs-card__text">{item.text}</span>
					</span>
				)}
			</span>
			{item.style === 'spotlight' && item.cta_label && targetIsLive(item.target) && (
				<span className="hs-card__cta">{item.cta_label}</span>
			)}
		</>
	)

	// Без строк вся карточка — одна ссылка. Со строками ссылки вкладывать нельзя (<a> в <a>), поэтому
	// карточка — обычный блок: её собственная цель лежит растянутой ссылкой под содержимым, строки — поверх.
	if (!lines.length) {
		return (
			<CardLink target={item.target} className={cls} style={style} goal={goal} aria-label={item.title}>
				{body}
			</CardLink>
		)
	}
	return (
		<div className={cls} style={style}>
			{targetIsLive(item.target) && (
				<CardLink target={item.target} className="hs-card__stretch" goal={goal} aria-label={item.title}>
					<span />
				</CardLink>
			)}
			{body}
			<ul className="hs-card__lines">
				{lines.map((line, i) => (
					<li key={`${line.title}-${i}`}>
						<CardLink
							target={line.target}
							className="hs-line"
							goal={{ name: GOAL, params: { block: blockId, card: item.id, line: line.title } }}
						>
							<LineIcon target={line.target} />
							<span className="hs-line__body">
								<span className="hs-line__title">
									{line.title}
									<StaffTag target={line.target} />
								</span>
								{line.text && <span className="hs-line__text">{line.text}</span>}
							</span>
						</CardLink>
					</li>
				))}
			</ul>
		</div>
	)
}

/** Пресет общего пула в сетке витрины: обложка/ролик карточки, название и описание; клик — туда же,
 *  куда ведёт цель «карточка» (лендинг, при закрытом разделе — генератор). */
function Preset({ item, blockId, textStyle }: { item: ShowcasePresetItem; blockId: string; textStyle?: string }) {
	const c = item.card
	const media: ShowcaseMedia = {
		image_url: c.background_url,
		image_preview_url: c.background_preview_url,
		video_url: c.video_url,
		video_preview_url: c.video_preview_url,
		video_loop_url: c.video_loop_url,
	}
	const large = !item.short && (item.cols === 2 || item.rows >= 2)
	return (
		<CardLink
			target={item.target}
			className={`hs-card hs-card--preset has-media${spanClass(item)}${mediaTextClasses(showcaseTextStyle(item.text_style, textStyle), item.tone)}`}
			goal={{ name: GOAL, params: { block: blockId, card: item.id, preset: c.id } }}
			aria-label={c.title}
		>
			<CardMedia media={media} large={large} />
			<span className="hs-card__head">
				{c.badge_text && <span className="hs-card__badge">{c.badge_text}</span>}
				<StaffTag target={item.target} />
			</span>
			<span className="hs-card__foot">
				<span className="hs-card__title">{c.title}</span>
				{c.description && (
					<span className="hs-card__reveal">
						<span className="hs-card__text">{c.description}</span>
					</span>
				)}
			</span>
		</CardLink>
	)
}

function Lead({ lead, blockId }: { lead: ShowcaseLead; blockId: string }) {
	const hasMedia = !!(lead.media?.image_url || lead.media?.video_url)
	return (
		<div className={`hs-lead hs-lead--${lead.style}${hasMedia ? ' has-media' : ''}`}>
			{lead.style === 'spotlight' && <CardMedia media={lead.media} large />}
			<div className="hs-lead__body">
				{lead.badge && <span className="hs-lead__badge">{lead.badge}</span>}
				<h3 className="hs-lead__title">
					{lead.title}
					{lead.title_muted && <span className="hs-lead__title-muted"> {lead.title_muted}</span>}
				</h3>
				{lead.text && <p className="hs-lead__text">{lead.text}</p>}
			</div>
			{lead.cta && targetIsLive(lead.cta.target) && (
				<CardLink
					target={lead.cta.target}
					className="hs-lead__cta"
					goal={{ name: GOAL, params: { block: blockId, card: 'lead', title: lead.cta.label } }}
				>
					{lead.cta.label}
					<StaffTag target={lead.cta.target} />
				</CardLink>
			)}
		</div>
	)
}

const isCard = (item: ShowcaseItem): item is ShowcaseCardItem => item.type !== 'preset'

/** «Витрина»: вводная колонка и бенто-сетка уникальных карточек (см. backend/app/services/page_blocks.py). */
export default function ShowcaseBlock({ blockId, config }: Props) {
	const headingId = useId()
	const items = Array.isArray(config.items) ? config.items : []
	const lead = config.lead
	if (!items.length && !lead) return null
	const cols = showcaseColumns(config)
	const labelled = config.title ? headingId : undefined
	return (
		<section
			className={`hs hs--cols-${cols} hs--hover-${showcaseHover(config)}${config.width === 'wide' ? ' hs--wide' : ''}${lead ? ` hs--lead-${lead.style}` : ''}`}
			aria-labelledby={labelled}
			aria-label={labelled ? undefined : lead?.title}
		>
			{config.title && (
				<header className="hs__head">
					<h2 id={headingId} className="hs__title">{config.title}</h2>
					{config.subtitle && <p className="hs__subtitle">{config.subtitle}</p>}
				</header>
			)}
			<div className="hs__body">
				{lead && <Lead lead={lead} blockId={blockId} />}
				<div className="hs__grid">
					{showcaseSlots(items).map(({ slot, at }) => {
						const tiles = at.map((i) => {
							const item = items[i]
							return isCard(item) ? <Card key={item.id} item={item} blockId={blockId} textStyle={config.text_style} /> : <Preset key={item.id} item={item} blockId={blockId} textStyle={config.text_style} />
						})
						if (!slot) return tiles[0]
						// Ячейка узких: одна обычная клетка сетки, узкие в ней друг под другом (см. showcaseSlots).
						const first = items[at[0]]
						return (
							<div key={`slot-${first.id}`} className={`hs-slot${first.cols === 2 ? ' is-c2' : ''}`}>
								{tiles}
							</div>
						)
					})}
				</div>
			</div>
		</section>
	)
}
